import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  DataGridPro,
  GridSelectionModel,
  GridValueGetterParams
} from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useGetMyPmxActivitiesFavoritesQuery } from "../../backend/hooks/pmxActivity/favorite/queryGetMyPmxActivitiesFavorites";
import { useDeletePmxActivityMutation } from "../../backend/hooks/pmxActivity/mutationDeletePmxActivity";
import { PmxActivity } from "../../backend/types";
import { customFilters } from "../../helpers/tableViewHelper";
import { useAuth } from "../authContextProvider";
import AccessRightDetails from "./accessRightDetails";
import EditActivityModal from "./editActivityModal";
import FavoriteButton from "./favoriteButton";
import Error from "../abstractComponents/error";
import { Link } from "react-router-dom";

// Component that lists all the PMX activities provided by the parent
export function ListActivities(props: {
  activities: [PmxActivity] | undefined;
  onSelectedActivityChange?: (activity: PmxActivity | null) => void;
}) {
  const { isAdminUser } = useAuth();

  const handleClickFolder = (
    event: React.MouseEvent<HTMLButtonElement>,
    pmxActivity: PmxActivity
  ) => {
    setAnchorElFolder(event.currentTarget);
    setCurrentPmxActivity(pmxActivity);
  };

  const [currentPmxActivity, setCurrentPmxActivity] =
    useState<PmxActivity | null>(null);
  const [anchorElFolder, setAnchorElFolder] = useState<null | HTMLElement>(
    null
  );
  const [isAccessRightOpen, setIsAccessRightOpen] = useState(false);
  const [isEditActivityOpen, setIsEditActivityOpen] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  const { deletePmxActivityMutation, errorDeletePmxActivityMutation } =
    useDeletePmxActivityMutation();

  const { favoriteActivities, errorFavoriteActivities } =
    useGetMyPmxActivitiesFavoritesQuery();

  function deletePmxActivity() {
    setAnchorElFolder(null);
    if (
      window.confirm(
        `Do you confirm that you want to delete ${currentPmxActivity?.trialNumber} activity?`
      )
    )
      deletePmxActivityMutation({
        variables: {
          id: Number(currentPmxActivity?.id)
        },
        refetchQueries: ["getMyPmxActivities"]
      });
  }

  const columns = [
    {
      field: "trialNumber",
      headerName: "Activity Name",
      filterOperators: customFilters(true),
      flex: 6,
      renderCell: (params: any) => (
        <div key={params.row.trialNumber} style={{ width: "100%" }}>
          <div className="button_without_text_transform float-start">
            <Link
              className="link_center_align my-2"
              to={`/activity/${params.row.id}`}
            >
              {params.row.mainRepository.isStandard ? (
                <LocalActivityIcon
                  className="secondary_color is_standard_activity"
                  style={{ fontSize: 40 }}
                />
              ) : (
                <LocalActivityOutlinedIcon
                  className="secondary_color is_private_activity"
                  style={{ fontSize: 40 }}
                />
              )}
              {params.row.trialNumber}
            </Link>
          </div>
          <Button
            variant="link"
            className="float-end inner_menu_button"
            onClick={(e) => handleClickFolder(e, params.row)}
            data-testid="inner_menu_button"
            data-activityname={params.row.trialNumber}
          >
            <MoreVertIcon
              className="dot_menu_browse_repository inner_icon_menu_button grey_color my-2"
              data-testid="dot_menu_browse_repository"
            />
          </Button>
        </div>
      )
    },
    {
      field: "favorite",
      headerName: "Favorite",
      flex: 1,
      renderCell: (params: any) => (
        <FavoriteButton
          activity={params.row}
          isFavorited={favoriteActivities?.some(
            (favActivity: PmxActivity) => favActivity.id === params.row.id
          )}
        />
      )
    },
    {
      field: "bayNumber",
      headerName: "BayNumber",
      filterOperators: customFilters(true),
      flex: 2
    },
    {
      field: "projectId",
      headerName: "Project ID",
      filterOperators: customFilters(true),
      flex: 2
    },
    {
      field: "repository",
      headerName: "Type",
      filterOperators: customFilters(true),
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.mainRepository.isStandard ? "Standard" : "Private"
    },
    {
      field: "trialShortDescription",
      headerName: "Trial Description",
      filterOperators: customFilters(true),
      flex: 2,
      hide: true
    },
    {
      field: "id",
      headerName: "ID",
      hide: true
    }
  ];

  const handleCloseFolder = () => {
    setAnchorElFolder(null);
  };

  const openAccessRightModal = () => {
    setIsAccessRightOpen(true);
    setAnchorElFolder(null);
    document.getElementsByTagName("body")[0].style.overflow = "auto";
  };

  const openEditActivityModal = () => {
    setIsEditActivityOpen(true);
    setAnchorElFolder(null);
    document.getElementsByTagName("body")[0].style.overflow = "auto";
  };

  const rows = useMemo(
    () =>
      props.activities?.map((activity) => ({
        ...activity,
        favorite:
          favoriteActivities?.some(
            (favActivity: PmxActivity) => favActivity.id === activity.id
          ) ?? false
      })) ?? [],
    [props.activities, favoriteActivities]
  );

  const handleSelectionModelChange = (selection: GridSelectionModel) => {
    // Enforce single selection: keep only the newly clicked row, or clear when unchecked
    let nextSelection: GridSelectionModel;
    if (selection.length > 1) {
      const newlySelected = selection.filter(
        (id) => !selectionModel.includes(id)
      );
      nextSelection =
        newlySelected.length > 0
          ? [newlySelected[newlySelected.length - 1]]
          : [selection[selection.length - 1]];
    } else {
      nextSelection = selection;
    }

    setSelectionModel(nextSelection);

    const selectedId = nextSelection[0];
    const selectedActivity =
      props.activities?.find((activity) => activity.id === selectedId) ?? null;
    props.onSelectedActivityChange?.(selectedActivity ?? null);
  };

  return (
    <div className="list_repositories_container">
      {rows.length > 0 ? (
        <DataGridPro
          rows={rows || []}
          columns={columns}
          pageSize={props.onSelectedActivityChange ? 10 : 50}
          rowHeight={60}
          disableSelectionOnClick
          autoHeight
          getRowHeight={() => "auto"}
          checkboxSelection={props.onSelectedActivityChange ? true : false}
          selectionModel={props.onSelectedActivityChange ? selectionModel : []}
          onSelectionModelChange={
            props.onSelectedActivityChange
              ? handleSelectionModelChange
              : undefined
          }
          hideFooterSelectedRowCount
          initialState={{
            sorting: {
              sortModel: [
                { field: "favorite", sort: "desc" },
                { field: "id", sort: "asc" }
              ]
            }
          }}
          sx={
            // this disables the checkbox column header, that can select all rows at once
            props.onSelectedActivityChange
              ? {
                  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
                    display: "none"
                  }
                }
              : undefined
          }
        />
      ) : (
        <div className="no_repositories_available_div">
          No Activities are accessible
        </div>
      )}
      <Menu
        id="simple-menu"
        anchorEl={anchorElFolder}
        open={Boolean(anchorElFolder)}
        onClose={handleCloseFolder}
        PaperProps={{
          style: {
            boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
            marginTop: "15px",
            marginLeft: "5px"
          }
        }}
        data-testid="simple-menu"
      >
        {isAdminUser && (
          <MenuItem id="delete_repository" onClick={deletePmxActivity}>
            Delete Activity
          </MenuItem>
        )}
        <MenuItem
          id="view_access_rights"
          data-testid="view_access_rights"
          onClick={openAccessRightModal}
        >
          Access Rights
        </MenuItem>
        <MenuItem
          id="edit_activity_menu_item"
          data-testid="edit_activity_menu_item"
          onClick={openEditActivityModal}
        >
          Edit PMX Activity
        </MenuItem>
      </Menu>
      {isAccessRightOpen && (
        <div>
          <AccessRightDetails
            repo={currentPmxActivity?.mainRepository}
            setIsAccessRightOpen={setIsAccessRightOpen}
          />
        </div>
      )}
      {isEditActivityOpen && (
        <EditActivityModal
          isEditWindowOpen={isEditActivityOpen}
          setIsEditWindowOpen={setIsEditActivityOpen}
          activityData={currentPmxActivity}
        />
      )}
      {(errorFavoriteActivities || errorDeletePmxActivityMutation) && (
        <Error
          error={errorFavoriteActivities || errorDeletePmxActivityMutation}
        />
      )}
    </div>
  );
}
